import { useEffect, useState } from "react";
import {
  Plus,
  Building2,
  Eye,
  LogOut,
  KeyRound,
  X,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import PageHeader from "../../components/PageHeader";

const STATUSES = ["ACTIVE", "SUSPENDED", "EXPIRED"];

const STATUS_STYLE = {
  ACTIVE: "bg-emerald-500/15 text-emerald-400",
  SUSPENDED: "bg-red-500/15 text-red-400",
  EXPIRED: "bg-red-500/15 text-red-400",
};

export default function PlatformTenantsPage() {
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    name: "",
    brand_name: "",
    owner_name: "",
    owner_email: "",
    owner_password: "",
    plan_code: "FREE",
  });

  const [subEdit, setSubEdit] = useState(null);
  const [subForm, setSubForm] = useState({
    plan_code: "",
    status: "",
    reason: "",
  });

  const [history, setHistory] = useState(null);

  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [resetOwner, setResetOwner] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const load = () => {
    api
      .get("/platform/tenants")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(apiError(e)));

    api
      .get("/platform/plans")
      .then((r) => setPlans(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();

    try {
      await api.post("/platform/tenants", form);
      toast.success("Tenant created");
      setShow(false);

      setForm({
        name: "",
        brand_name: "",
        owner_name: "",
        owner_email: "",
        owner_password: "",
        plan_code: "FREE",
      });

      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const changeSub = async () => {
    try {
      await api.patch(`/platform/tenants/${subEdit.id}/subscription`, {
        plan_code: subForm.plan_code || undefined,
        status: subForm.status || undefined,
        reason: subForm.reason,
      });

      toast.success("Subscription updated");
      setSubEdit(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openHistory = async (t) => {
    try {
      const { data } = await api.get(
        "/platform/subscription-history",
        {
          params: {
            tenant_id: t.id,
          },
        }
      );

      setHistory({
        tenant: t,
        items: data,
      });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openDetails = async (t) => {
    setDetailsLoading(true);

    try {
      const { data } = await api.get(
        `/platform/tenants/${t.id}/details`
      );

      setDetails(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDetailsLoading(false);
    }
  };

  const openResetOwner = () => {
    if (!details?.owner) {
      toast.error("Owner not found");
      return;
    }

    setResetPassword("");
    setResetPasswordConfirm("");
    setResetOwner(details.owner);
  };

  const submitResetOwner = async (e) => {
    e.preventDefault();

    if (!resetPassword) {
      toast.error("New password is required");
      return;
    }

    if (resetPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (resetPassword !== resetPasswordConfirm) {
      toast.error("Password confirmation does not match");
      return;
    }

    setResetLoading(true);

    try {
      await api.post(
        `/platform/tenants/${details.tenant.id}/owner/reset-password`,
        {
          new_password: resetPassword,
        }
      );

      toast.success(
        "Owner password reset. Existing sessions have been revoked."
      );

      setResetOwner(null);
      setResetPassword("");
      setResetPasswordConfirm("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setResetLoading(false);
    }
  };

  const forceLogoutOwner = async (t) => {
    const confirmed = window.confirm(
      `Force logout Owner untuk tenant "${t.name}"?\n\nSemua session login Owner akan dibatalkan.`
    );

    if (!confirmed) {
      return;
    }

    try {
      await api.post(
        `/platform/tenants/${t.id}/owner/force-logout`
      );

      toast.success(
        "Owner force logout berhasil. Semua session Owner telah dibatalkan."
      );
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="platform-tenants-page">
      <PageHeader
        title="Tenants"
        subtitle="Businesses on the GLOO platform"
        testid="platform-tenants-header"
      >
        <button
          onClick={() => setShow(true)}
          data-testid="tenant-create-button"
          className="h-10 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-bold flex items-center gap-2"
        >
          <Plus size={16} />
          New Tenant
        </button>
      </PageHeader>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">Tenant</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 hidden md:table-cell">
                Usage
              </th>
              <th className="px-4 py-3 hidden md:table-cell">
                Renews
              </th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {items.map((t) => (
              <tr
                key={t.id}
                data-testid={`tenant-row-${t.code}`}
                className="border-b border-border/50 hover:bg-secondary/30"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
                      <Building2 size={15} />
                    </div>

                    <div>
                      <p className="font-semibold">{t.name}</p>
                      <p className="text-[11px] text-muted-foreground font-mono">
                        {t.code}
                      </p>
                    </div>
                  </div>
                </td>

                <td className="px-4 py-3">
                  <span className="px-2 py-1 rounded-md bg-primary/15 text-primary text-[11px] font-bold">
                    {t.subscription?.plan_code}
                  </span>
                </td>

                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-1 rounded-md text-[11px] font-bold ${
                      STATUS_STYLE[t.subscription?.status] ||
                      "bg-secondary"
                    }`}
                  >
                    {t.subscription?.status}
                  </span>
                </td>

                <td className="px-4 py-3 hidden md:table-cell text-xs text-muted-foreground">
                  {t.outlets_count} outlets · {t.users_count} active users
                </td>

                <td className="px-4 py-3 hidden md:table-cell text-xs">
                  {fmtDate(t.subscription?.current_period_end)}
                </td>

                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => openDetails(t)}
                    data-testid={`tenant-details-${t.code}`}
                    className="text-xs text-foreground font-bold hover:underline mr-3 inline-flex items-center gap-1"
                  >
                    <Eye size={13} />
                    Details
                  </button>

                  <button
                    onClick={() => {
                      setSubEdit(t);
                      setSubForm({
                        plan_code: t.subscription?.plan_code,
                        status: t.subscription?.status,
                        reason: "",
                      });
                    }}
                    data-testid={`tenant-sub-edit-${t.code}`}
                    className="text-xs text-primary font-bold hover:underline mr-3"
                  >
                    Subscription
                  </button>

                  <button
                    onClick={() => forceLogoutOwner(t)}
                    data-testid={`tenant-force-logout-${t.code}`}
                    className="text-xs text-amber-400 font-bold hover:underline mr-3 inline-flex items-center gap-1"
                  >
                    <LogOut size={13} />
                    Force Logout
                  </button>

                  <button
                    onClick={() => openHistory(t)}
                    data-testid={`tenant-history-${t.code}`}
                    className="text-xs text-muted-foreground font-bold hover:underline"
                  >
                    History
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShow(false)}
        >
          <form
            onSubmit={create}
            className="bg-card border border-border w-full max-w-md rounded-2xl p-6 space-y-3 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="tenant-create-modal"
          >
            <h3 className="font-heading text-lg font-bold">
              New Tenant
            </h3>

            <input
              data-testid="tenant-form-name"
              value={form.name}
              onChange={(e) =>
                setForm({
                  ...form,
                  name: e.target.value,
                })
              }
              placeholder="Business name"
              required
              className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
            />

            <input
              data-testid="tenant-form-brand"
              value={form.brand_name}
              onChange={(e) =>
                setForm({
                  ...form,
                  brand_name: e.target.value,
                })
              }
              placeholder="Brand name (shown in POS)"
              className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
            />

            <input
              data-testid="tenant-form-owner-name"
              value={form.owner_name}
              onChange={(e) =>
                setForm({
                  ...form,
                  owner_name: e.target.value,
                })
              }
              placeholder="Owner name"
              required
              className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
            />

            <input
              data-testid="tenant-form-owner-email"
              type="email"
              value={form.owner_email}
              onChange={(e) =>
                setForm({
                  ...form,
                  owner_email: e.target.value,
                })
              }
              placeholder="Owner email"
              required
              className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
            />

            <input
              data-testid="tenant-form-owner-password"
              value={form.owner_password}
              onChange={(e) =>
                setForm({
                  ...form,
                  owner_password: e.target.value,
                })
              }
              placeholder="Owner password"
              required
              className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
            />

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-muted-foreground">
                  Subscription Plan
                </label>

                <select
                  data-testid="tenant-form-plan"
                  value={form.plan_code}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      plan_code: e.target.value,
                    })
                  }
                  className="h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
                >
                  {plans.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1" />
            </div>

            <button
              type="submit"
              data-testid="tenant-form-submit"
              className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm"
            >
              Create Tenant
            </button>
          </form>
        </div>
      )}

      {subEdit && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSubEdit(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Subscription for ${subEdit.name}`}
            className="bg-card border border-border w-full max-w-md rounded-2xl p-6 space-y-3"
            onClick={(e) => e.stopPropagation()}
            data-testid="subscription-edit-modal"
          >
            <h3 className="font-heading text-lg font-bold">
              Subscription · {subEdit.name}
            </h3>

            <select
              data-testid="sub-edit-plan"
              value={subForm.plan_code}
              onChange={(e) =>
                setSubForm({
                  ...subForm,
                  plan_code: e.target.value,
                })
              }
              className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
            >
              {plans.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name}
                </option>
              ))}
            </select>

            <select
              data-testid="sub-edit-status"
              value={subForm.status}
              onChange={(e) =>
                setSubForm({
                  ...subForm,
                  status: e.target.value,
                })
              }
              className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <input
              data-testid="sub-edit-reason"
              value={subForm.reason}
              onChange={(e) =>
                setSubForm({
                  ...subForm,
                  reason: e.target.value,
                })
              }
              placeholder="Reason (recorded in history)"
              className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
            />

            <button
              onClick={changeSub}
              data-testid="sub-edit-submit"
              className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm"
            >
              Apply Change
            </button>
          </div>
        </div>
      )}

      {detailsLoading && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl px-6 py-5 text-sm font-semibold">
            Loading tenant details...
          </div>
        </div>
      )}

      {details && !detailsLoading && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDetails(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Details for ${details.tenant?.name || "Tenant"}`}
            className="bg-card border border-border w-full max-w-4xl rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="tenant-details-modal"
          >
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h3 className="font-heading text-xl font-bold">
                  Tenant Details
                </h3>

                <p className="text-sm text-muted-foreground mt-1">
                  {details.tenant?.name} ·{" "}
                  <span className="font-mono">
                    {details.tenant?.code}
                  </span>
                </p>
              </div>

              <button
                onClick={() => setDetails(null)}
                className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center hover:bg-secondary/70"
                aria-label="Close"
              >
                <X size={17} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <section className="bg-secondary/30 border border-border rounded-xl p-4">
                <h4 className="text-sm font-bold mb-3">
                  Tenant Information
                </h4>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">
                      Tenant
                    </span>
                    <span className="font-semibold text-right">
                      {details.tenant?.name || "—"}
                    </span>
                  </div>

                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">
                      Code
                    </span>
                    <span className="font-mono">
                      {details.tenant?.code || "—"}
                    </span>
                  </div>

                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">
                      Plan
                    </span>
                    <span className="font-semibold">
                      {details.subscription?.plan_code || "—"}
                    </span>
                  </div>

                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">
                      Subscription
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${
                        STATUS_STYLE[
                          details.subscription?.status
                        ] || "bg-secondary"
                      }`}
                    >
                      {details.subscription?.status || "—"}
                    </span>
                  </div>

                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">
                      Renews
                    </span>
                    <span>
                      {fmtDate(
                        details.subscription?.current_period_end
                      )}
                    </span>
                  </div>
                </div>
              </section>

              <section className="bg-secondary/30 border border-border rounded-xl p-4">
                <h4 className="text-sm font-bold mb-3">
                  Usage
                </h4>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-card border border-border rounded-xl p-3">
                    <p className="text-xs text-muted-foreground">
                      Outlets
                    </p>
                    <p className="text-xl font-bold mt-1">
                      {details.usage?.outlets?.active ?? 0}
                      <span className="text-sm font-normal text-muted-foreground">
                        {" "}
                        /{" "}
                        {details.usage?.outlets?.registered ?? 0}
                      </span>
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      active / registered
                    </p>
                  </div>

                  <div className="bg-card border border-border rounded-xl p-3">
                    <p className="text-xs text-muted-foreground">
                      Users
                    </p>
                    <p className="text-xl font-bold mt-1">
                      {details.usage?.users?.active ?? 0}
                      <span className="text-sm font-normal text-muted-foreground">
                        {" "}
                        /{" "}
                        {details.usage?.users?.registered ?? 0}
                      </span>
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      active / registered
                    </p>
                  </div>
                </div>
              </section>
            </div>

            <section className="bg-secondary/30 border border-border rounded-xl p-4 mt-4">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <h4 className="text-sm font-bold">
                    Owner
                  </h4>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Primary tenant owner account
                  </p>
                </div>

                {details.owner && (
                  <button
                    onClick={openResetOwner}
                    data-testid="owner-reset-password-button"
                    className="h-9 px-3 rounded-lg bg-primary text-primary-foreground text-xs font-bold inline-flex items-center gap-2"
                  >
                    <KeyRound size={14} />
                    Reset Password
                  </button>
                )}
              </div>

              {details.owner ? (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Name
                    </p>
                    <p className="font-semibold mt-1">
                      {details.owner.name || "—"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">
                      Email
                    </p>
                    <p className="font-semibold mt-1 break-all">
                      {details.owner.email || "—"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">
                      Role
                    </p>
                    <p className="font-semibold mt-1">
                      {details.owner.role || "OWNER"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">
                      Status
                    </p>
                    <p
                      className={`font-semibold mt-1 ${
                        details.owner.active
                          ? "text-emerald-400"
                          : "text-red-400"
                      }`}
                    >
                      {details.owner.active
                        ? "ACTIVE"
                        : "INACTIVE"}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No OWNER account found.
                </p>
              )}
            </section>

            <section className="mt-4">
              <h4 className="text-sm font-bold mb-3">
                Users
              </h4>

              <div className="border border-border rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="px-3 py-2">
                          User
                        </th>
                        <th className="px-3 py-2">
                          Role
                        </th>
                        <th className="px-3 py-2">
                          Status
                        </th>
                        <th className="px-3 py-2 hidden md:table-cell">
                          Outlets
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {(details.users || []).map((u) => (
                        <tr
                          key={u.id}
                          className="border-b border-border/50 last:border-0"
                        >
                          <td className="px-3 py-2">
                            <p className="font-semibold">
                              {u.name || "—"}
                            </p>
                            <p className="text-[11px] text-muted-foreground">
                              {u.email || "—"}
                            </p>
                          </td>

                          <td className="px-3 py-2 text-xs font-semibold">
                            {u.role || "—"}
                          </td>

                          <td className="px-3 py-2">
                            <span
                              className={`px-2 py-1 rounded-md text-[11px] font-bold ${
                                u.active
                                  ? "bg-emerald-500/15 text-emerald-400"
                                  : "bg-red-500/15 text-red-400"
                              }`}
                            >
                              {u.active
                                ? "ACTIVE"
                                : "INACTIVE"}
                            </span>
                          </td>

                          <td className="px-3 py-2 hidden md:table-cell text-xs text-muted-foreground">
                            {(u.outlet_ids || []).length || 0}
                          </td>
                        </tr>
                      ))}

                      {(details.users || []).length === 0 && (
                        <tr>
                          <td
                            colSpan="4"
                            className="px-3 py-5 text-center text-sm text-muted-foreground"
                          >
                            No users found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <section className="mt-4">
              <h4 className="text-sm font-bold mb-3">
                Outlets
              </h4>

              <div className="border border-border rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="px-3 py-2">
                          Code
                        </th>
                        <th className="px-3 py-2">
                          Outlet
                        </th>
                        <th className="px-3 py-2">
                          Status
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {(details.outlets || []).map((o) => (
                        <tr
                          key={o.id}
                          className="border-b border-border/50 last:border-0"
                        >
                          <td className="px-3 py-2 font-mono text-xs">
                            {o.code || "—"}
                          </td>

                          <td className="px-3 py-2 font-semibold">
                            {o.name || "—"}
                          </td>

                          <td className="px-3 py-2">
                            <span
                              className={`px-2 py-1 rounded-md text-[11px] font-bold ${
                                o.active
                                  ? "bg-emerald-500/15 text-emerald-400"
                                  : "bg-red-500/15 text-red-400"
                              }`}
                            >
                              {o.active
                                ? "ACTIVE"
                                : "INACTIVE"}
                            </span>
                          </td>
                        </tr>
                      ))}

                      {(details.outlets || []).length === 0 && (
                        <tr>
                          <td
                            colSpan="3"
                            className="px-3 py-5 text-center text-sm text-muted-foreground"
                          >
                            No outlets found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}

      {resetOwner && (
        <div
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => {
            if (!resetLoading) {
              setResetOwner(null);
            }
          }}
        >
          <form
            onSubmit={submitResetOwner}
            className="bg-card border border-border w-full max-w-md rounded-2xl p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="owner-reset-password-modal"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-lg font-bold">
                  Reset Owner Password
                </h3>

                <p className="text-sm text-muted-foreground mt-1">
                  {resetOwner.name} · {resetOwner.email}
                </p>
              </div>

              <button
                type="button"
                disabled={resetLoading}
                onClick={() => setResetOwner(null)}
                className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center"
              >
                <X size={17} />
              </button>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-xs text-amber-300">
              Reset password akan sekaligus membatalkan semua
              session login Owner yang sedang aktif.
            </div>

            <input
              type="password"
              autoComplete="new-password"
              value={resetPassword}
              onChange={(e) =>
                setResetPassword(e.target.value)
              }
              placeholder="New password"
              minLength={8}
              required
              className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
            />

            <input
              type="password"
              autoComplete="new-password"
              value={resetPasswordConfirm}
              onChange={(e) =>
                setResetPasswordConfirm(e.target.value)
              }
              placeholder="Confirm new password"
              minLength={8}
              required
              className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
            />

            <button
              type="submit"
              disabled={resetLoading}
              className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm disabled:opacity-50"
            >
              {resetLoading
                ? "Resetting..."
                : "Reset Owner Password"}
            </button>
          </form>
        </div>
      )}

      {history && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setHistory(null)}
        >
          <div
            className="bg-card border border-border w-full max-w-lg rounded-2xl p-6 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="subscription-history-modal"
          >
            <h3 className="font-heading text-lg font-bold mb-3">
              History · {history.tenant.name}
            </h3>

            <div className="space-y-2">
              {history.items.map((h) => (
                <div
                  key={h.id}
                  className="bg-secondary/40 rounded-xl p-3 text-xs"
                >
                  <p className="font-semibold">
                    {h.old_plan_code || "—"} →{" "}
                    {h.new_plan_code} ·{" "}
                    {h.old_status || "—"} →{" "}
                    {h.new_status}
                  </p>

                  <p className="text-muted-foreground mt-0.5">
                    {h.reason || "No reason"} · by{" "}
                    {h.changed_by} · {h.source}
                  </p>

                  <p className="text-muted-foreground/70 mt-0.5">
                    {fmtDate(h.created_at)}
                  </p>
                </div>
              ))}

              {history.items.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No history.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
